<!-- BaseDataView.vue -->
<template>
  <div class="base-data-view">
    <div class="button-group">
      <div>
        <el-button
            v-for="button in config.buttons"
            :key="button.text"
            :type="button.type"
            :disabled="button.disabled ? button.disabled(context) : false"
            @click="() => button.handler(context)"
        >
          {{ button.text }}
        </el-button>
      </div>
      <div>
        <el-input
            v-model="searchKeyword"
            style="width: 240px"
            :placeholder="config.searchPlaceholder || '请输入关键字'"
        />
        <el-button type="primary" style="margin-left: 10px" @click="handleSearch">查询</el-button>
      </div>
    </div>

    <div class="table-container" ref="tableContainerRef">
      <el-table
          :data="tableData"
          border
          :height="tableHeight"
          @selection-change="handleSelectionChange"
          :row-class-name="rowClassName"
          style="width: 100%;"
      >
        <el-table-column type="index" label="" width="60" align="center" fixed />
        <el-table-column type="selection" width="55" v-if="config.selectable" fixed />
        <el-table-column
            v-for="column in config.columns"
            :key="column.prop"
            :prop="column.prop"
            :label="column.label"
            :min-width="column.width || 120"
            :align="column.align || (column.type === 'number' || column.type === 'date' ? 'right' : 'left')"
            header-align="center"
            :show-overflow-tooltip="!column.customTooltip"
        >
          <template #default="scope">
            <!-- 自定义高级 Tooltip -->
            <el-tooltip
              v-if="column.customTooltip"
              placement="top"
              :show-after="800"
              effect="dark"
              popper-class="glass-tooltip-popper"
            >
              <template #content>
                <div class="rich-tooltip-content">
                  {{ column.formatter ? column.formatter(scope.row, column) : scope.row[column.prop] }}
                </div>
              </template>
              <div class="truncate-cell-content">
                {{ column.formatter ? column.formatter(scope.row, column) : scope.row[column.prop] }}
              </div>
            </el-tooltip>

            <!-- 普通显示 -->
            <template v-else>
              <!-- 标签类型 -->
              <template v-if="column.type === 'tag'">
                 <el-tag :type="column.getTagType ? column.getTagType(scope.row[column.prop], scope.row) : ''" effect="light" round>
                    {{ column.formatter ? column.formatter(scope.row, column) : scope.row[column.prop] }}
                 </el-tag>
              </template>
              
              <!-- 图片类型 -->
              <template v-else-if="column.type === 'image'">
                <el-image 
                  v-if="scope.row[column.prop]" 
                  :src="scope.row[column.prop]" 
                  :preview-src-list="[scope.row[column.prop]]"
                  preview-teleported
                  fit="cover" 
                  style="width: 50px; height: 50px; border-radius: 4px; display: block;"
                />
                <span v-else class="text-xs text-gray-400">-</span>
              </template>

              <!-- 开关类型 -->
              <template v-else-if="column.type === 'switch'">
                <el-switch
                  v-model="scope.row[column.prop]"
                  @change="(val) => column.onChange && column.onChange(val, scope.row, context)"
                />
              </template>
              
              <template v-else-if="column.formatter">
                {{ column.formatter(scope.row, column) }}
              </template>
              <template v-else>
                {{ scope.row[column.prop] }}
              </template>
            </template>
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column v-if="config.tableActions" :label="config.tableActions.columnName" :width="config.tableActions.width">
          <template #default="scope">
            <div class="action-buttons">
              <template v-for="(btn, index) in config.tableActions.buttons" :key="index">
                <!-- 下拉菜单按钮 -->
                <el-dropdown v-if="btn.children && btn.children.length > 0" trigger="click" style="margin-left: 8px;">
                  <el-button :type="btn.type || 'default'" :size="btn.size || 'small'" link>
                    {{ btn.text }} <i class="iconfont icon-arrow-down" style="font-size: 12px; margin-left: 2px;"></i>
                  </el-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item
                         v-for="(child, cIndex) in btn.children"
                         :key="cIndex"
                         @click="child.handler(scope.row, context)"
                      >
                         <span :style="{ color: child.type === 'danger' ? '#f56c6c' : 'inherit' }">{{ child.text }}</span>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>

                <!-- 普通按钮 -->
                <el-button
                    v-else
                    :type="btn.type || 'primary'"
                    :size="btn.size || 'small'"
                    :icon="btn.icon"
                    :link="btn.link !== undefined ? btn.link : true"
                    :plain="btn.plain"
                    @click="btn.handler(scope.row, context)"
                >
                  {{ btn.text }}
                </el-button>
              </template>
            </div>
          </template>
        </el-table-column>

      </el-table>
    </div>

    <div class="table-footer">
      <div class="data-info">
        选择 {{ selectedRows ? selectedRows.length : 0 }} / {{ tableData ? tableData.length : 0 }} 条数据
      </div>
    </div>

    <!-- 动态对话框组件：如果配置了dialogComponent，使用指定组件 -->
    <component
        v-if="config.dialog && config.dialogComponent && dialogComponentLoader"
        :is="dialogComponentLoader"
        v-model="dialogVisible"
        :formData="formData"
        :config="config"
        @submit="handleDialogConfirm"
    />

    <!-- 默认内联对话框（向后兼容） -->
    <el-dialog
        v-else-if="config.dialog"
        :title="formData.id ? '编辑' : '新建'"
        v-model="dialogVisible"
        :width="config.dialog.width || '50%'"
        :before-close="handleDialogClose"
        draggable
        :close-on-click-modal="false"
    >
      <el-form
          ref="formRef"
          :model="formData"
          :rules="config.rules"
          label-width="80px"
      >
        <el-row :gutter="20">
          <el-col v-for="field in config.dialog.fields" :key="field.prop" :span="field.span || 12" v-show="field.type !== 'hidden'">
            <el-form-item
                :label="field.label"
                :prop="field.prop"
                :rules="config.rules[field.prop]"
            >
              <!-- 自动完成输入框 -->
              <template v-if="field.type === 'autocomplete'">
                <el-autocomplete
                    v-model="formData[field.prop]"
                    :fetch-suggestions="(query, cb) => handleAutoComplete(query, cb, field)"
                    :placeholder="field.props?.placeholder"
                    :value-key="field.props?.valueKey"
                    :disabled="field.disabled"
                    :trigger-on-focus="false"
                    :debounce="500"
                    @select="(item) => handleAutoCompleteSelect(item, field)"
                    class="input-field"
                    :name="field.prop"
                >
                <template #default="{ item }">
                    <div class="suggestion-item">
                      <div>{{ item.emid }} - {{ item.name }} - {{ item.teamgroupName }}</div>
                      <div class="suggestion-address">{{ item.address}}</div>
                    </div>
                  </template>
                </el-autocomplete>
              </template>
              <!-- 数字输入框 -->
              <template v-else-if="field.type === 'number'">
                <el-input-number
                    v-model="formData[field.prop]"
                    :disabled="field.disabled"
                    v-bind="field.props || {}"
                    @change="(value) => handleFieldChange(value, field, formData)"
                    class="input-field"
                    style="width: 100%"
                />
              </template>
              
              <!-- 图片/文件上传 -->
              <template v-else-if="field.type === 'image'">
                <el-upload
                  class="data-image-uploader"
                  action="/api/minio/upload"
                  :data="{ prefix: field.props?.prefix || 'data' }"
                  :show-file-list="false"
                  :on-success="(res) => { if (res && res.success) { formData[field.prop] = res.data; } else { ElMessage.error('上传失败'); } }"
                  :before-upload="(file) => {
                    const isImg = file.type.startsWith('image/');
                    if (!isImg) {
                      ElMessage.error('只能上传图片文件！');
                      return false;
                    }
                    const isLt2M = file.size / 1024 / 1024 < 2;
                    if (!isLt2M) {
                      ElMessage.error('上传图片大小不能超过 2MB!');
                      return false;
                    }
                    return true;
                  }"
                >
                  <div v-if="formData[field.prop]" class="relative group inline-block">
                    <img :src="formData[field.prop]" class="data-image-preview" style="max-width: 150px; max-height: 150px; border-radius: 6px; border: 1px dashed #dcdfe6; padding: 4px; display: block;" />
                    <!-- 删除按钮 -->
                    <div class="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity z-10 transform translate-x-1/3 -translate-y-1/3">
                      <div class="bg-red-500 text-white rounded-full p-1 cursor-pointer hover:bg-red-600 shadow-lg flex items-center justify-center" @click.stop="formData[field.prop] = ''">
                        <el-icon :size="14"><Delete /></el-icon>
                      </div>
                    </div>
                  </div>
                  <el-button v-else type="primary" size="small">上传图片</el-button>
                </el-upload>
              </template>
              <!-- 地图选点 -->
              <template v-else-if="field.type === 'location-picker'">
                  <div style="display: flex; gap: 10px; align-items: center; width: 100%;">
                      <el-input 
                          v-model="formData[field.prop]" 
                          :placeholder="field.props?.placeholder || '请输入地址'" 
                          :readonly="field.props?.readonly"
                          @click="openMapPicker"
                          style="flex: 1; min-width: 0; max-width: none;"
                      />
                      <el-button type="primary" @click="openMapPicker" style="flex-shrink: 0; width: 100px;">地图选点</el-button>
                  </div>
              </template>
              <!-- 复选框组 -->
              <template v-else-if="field.type === 'checkbox-group'">
                <el-checkbox-group v-model="formData[field.prop]" class="checkbox-group-with-labels">
                  <template v-for="(groupName, index) in getCheckboxGroups(field.options)" :key="index">
                    <div class="checkbox-group-section">
                      <div class="checkbox-group-label">{{ groupName }}</div>
                      <el-checkbox
                        v-for="option in getCheckboxOptionsByGroup(field.options, groupName)"
                        :key="option.value"
                        :label="option.value"
                        :disabled="field.disabled"
                      >
                        {{ option.label }}
                      </el-checkbox>
                    </div>
                  </template>
                </el-checkbox-group>
              </template>
              <!-- 其他类型的输入框 -->
              <template v-else>
                <component
                    :is="getComponentType(field.type)"
                    v-model="formData[field.prop]"
                    :disabled="field.disabled"
                    v-bind="field.props || {}"
                    :rows="field.rows || 5"
                    :type="field.type === 'textarea' ? 'textarea' : 'text'"
                    class="input-field"
                    :name="field.prop"
                    @input="(value) => handleFieldChange(value, field, formData)"
                >
                  <template v-if="field.type === 'select'" #default>
                    <!-- 支持分组 -->
                    <template v-if="field.options && field.options.length > 0 && field.options[0].options">
                       <el-option-group
                          v-for="group in field.options"
                          :key="group.label"
                          :label="group.label">
                          <el-option
                            v-for="item in group.options"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value">
                          </el-option>
                       </el-option-group>
                    </template>
                    <!-- 扁平列表 (兼容旧有配置) -->
                    <template v-else>
                      <el-option
                          v-for="option in field.options"
                          :key="option.value"
                          :label="option.label"
                          :value="option.value"
                      />
                    </template>
                  </template>
                </component>
              </template>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="validateAndSubmit">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog v-model="mapVisible" title="选择位置" width="800px" append-to-body destroy-on-close>
        <iframe id="mapPage" width="100%" height="500px" frameborder=0 
            :src="mapUrl">
        </iframe>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted, reactive, nextTick, computed, defineAsyncComponent } from 'vue';
import { useBaseDataOperations } from '@/composables/useBaseDataOperations';
import { ElMessage } from 'element-plus';
import mapConfig from '@/configs/mapConfig';
import { Delete } from '@element-plus/icons-vue';

const props = defineProps({
  config: Object,
  service: Object
});

// 动态组件加载器
const dialogComponentLoader = computed(() => {
  if (!props.config.dialogComponent) return null;
  
  // 使用 defineAsyncComponent 动态加载组件
  return defineAsyncComponent(() => 
    import(`@/components/${props.config.dialogComponent}.vue`)
  );
});

const mapUrl = computed(() => {
    // type=0: GCJ02 (Default/Stable)
    // policy=1: Allow interactive
    // coord: Default center (Wuhan) to ensure map renders if geolocation fails
    // _t: Timestamp to prevent cache
    let url = `https://apis.map.qq.com/tools/locpicker?search=1&type=0&policy=1&key=${mapConfig.key}&referer=${mapConfig.referer}`;
    
    // Validate coordinates
    const lat = parseFloat(formData.value.latitude);
    const lng = parseFloat(formData.value.longitude);

    console.log('BaseDataView Map Coords:', { lat, lng, rawLat: formData.value.latitude });

  // 严格验证经纬度，并保留6位小数
  if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && (Math.abs(lat) > 0.000001 || Math.abs(lng) > 0.000001)) {
    url += `&coord=${lat.toFixed(6)},${lng.toFixed(6)}`;
  } 
  // 否则不传 coord 参数，让地图自动定位或显示默认视图，避免"经纬度不合法"错误
  
  const finalUrl = url + `&_t=${Date.now()}`;
    console.log('BaseDataView Map URL:', finalUrl);
    return finalUrl;
});
// ... (imports and props) 

const mapVisible = ref(false);


const openMapPicker = () => {
    mapVisible.value = true;
};

// Map Listener
const handleMapMessage = (event) => {
    const loc = event.data;
    if (loc && loc.module === 'locationPicker') {
        console.log('Location selected (BaseDataView):', loc);
        if (loc.latng) {
            formData.value.latitude = loc.latng.lat;
            formData.value.longitude = loc.latng.lng;
        }
        if (loc.poiaddress) {
            formData.value.projectAddress = loc.poiaddress;
            if (loc.poiname && loc.poiname !== '我的位置') {
                 formData.value.projectAddress = loc.poiaddress + ' ' + loc.poiname;
            }
        }
        mapVisible.value = false;
        ElMessage.success('已选择位置');
    }
};

onMounted(() => {
    window.addEventListener('message', handleMapMessage, false);
});
onUnmounted(() => {
    window.removeEventListener('message', handleMapMessage);
});
// Main onMounted call follows below...


// 1. 先声明 formRef
const formRef = ref(null);

// 2. 然后是其他的 refs 和方法
const {
  tableData,
  searchKeyword,
  selectedRows,
  formData,
  dialogVisible,
  searchEmployees,
  fetchData,
  handleSearch,
  handleSelectionChange,
  handleSizeChange,
  handleCurrentChange,
  handleDelete,
  handleImport,
  handleExport,
  handleDialogClose,
  handleDialogConfirm,
  rowClassName
} = useBaseDataOperations(props.config, props.service);

// 3. 最后创 context
const context = reactive({
  service: props.service,
  formRef,  // 现在可以安全地引用 formRef
  tableData,
  searchKeyword,
  selectedRows,
  formData,
  dialogVisible,
  searchEmployees,  // 需要这个方法
  fetchData,
  handleSearch,
  handleSelectionChange,
  handleSizeChange,
  handleCurrentChange,
  handleDelete,
  handleImport,
  handleExport,
  handleDialogClose,
  handleDialogConfirm,
  rowClassName
});

const tableContainerRef = ref(null);

const getComponentType = (type) => {
  switch (type) {
    case 'select':
      return 'el-select';
    case 'textarea':
      return 'el-input';
    case 'date':
      return 'el-date-picker';
    case 'switch':
      return 'el-switch';
    default:
      return 'el-input';
  }
};

// 处理字段值变化
const handleFieldChange = (value, field, formData) => {
  console.log('字段值变化:', field.prop, value, formData);
  if (field.props && field.props.onChange) {
    // 使用 nextTick 确保值更新后再触发回调
    nextTick(() => {
      field.props.onChange(value, formData);
    });
  }
};

// 处理自动完成搜索
const handleAutoComplete = async (query, cb, field) => {
  console.log('触发自动完成搜索:', query);  // 添加日志
  if (field.props?.onSearch) {
    try {
      const suggestions = await field.props.onSearch(query, context);
      console.log('获取到建议:', suggestions);  // 添加日志
      cb(suggestions);
    } catch (error) {
      console.error('搜索失败:', error);
      cb([]);
    }
  } else {
    cb([]);
  }
};

// 处理自动完成选择
const handleAutoCompleteSelect = (item, field) => {
  console.log('选中建议项:', item);  // 添加日志
  if (field.props?.onSelect) {
    field.props.onSelect(item, context);
  }
};

// 获取checkbox分组
const getCheckboxGroups = (options) => {
  if (!options) return [];
  const groups = new Set();
  options.forEach(opt => {
    if (opt.group) {
      groups.add(opt.group);
    }
  });
  return Array.from(groups);
};

// 根据分组获取选项
const getCheckboxOptionsByGroup = (options, groupName) => {
  if (!options) return [];
  return options.filter(opt => opt.group === groupName);
};

const tableHeight = ref(0);

const calculateTableHeight = () => {
  if (tableContainerRef.value) {
    tableHeight.value = tableContainerRef.value.clientHeight;
  }
};

onMounted(() => {
  fetchData();
  calculateTableHeight();
  window.addEventListener('resize', calculateTableHeight);
});

onUnmounted(() => {
  window.removeEventListener('resize', calculateTableHeight);
});

watch(searchKeyword, () => {
  if (searchKeyword.value === '') {
    fetchData();
  }
});

// 新增验证和提交方法
const validateAndSubmit = async () => {
  if (!formRef.value) return;

  try {
    await formRef.value.validate();
    // 验证通过，继续提交
    handleDialogConfirm();
  } catch (error) {
    console.error('表单验证失败:', error);
    return false;
  }
};
</script>

<style>
.base-data-view {
  display: flex;
  flex-direction: column;
  height: calc(100% - 32px); /* Subtract margin */
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  margin: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.button-group {
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  background-color: #fff;
}

.table-container {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0; /* Remove padding if any */
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px; /* Reduced vertical padding */
  border-top: 1px solid #f1f5f9;
  background-color: #fff;
  height: 40px; /* Fixed smaller height */
}

.data-info {
  font-size: 12px; /* Smaller font */
  color: #64748b;
}

/* Modern Table Styles */
.el-table {
  --el-table-header-bg-color: #f8fafc;
  --el-table-border-color: #e2e8f0;
  --el-table-text-color: #334155;
  --el-table-header-text-color: #475569;
}

.el-table th.el-table__cell {
  background-color: var(--el-table-header-bg-color) !important;
  color: var(--el-table-header-text-color);
  font-weight: 600;
  height: 48px;
}

.el-table td.el-table__cell {
  padding: 8px 0;
}

/* Remove side borders for cleaner look */
.el-table--border, .el-table--group {
  border: none;
}
.el-table__inner-wrapper::before {
  display: none; /* Remove bottom border */
}

/* Custom Scrollbar styled for table */
.table-container::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.table-container::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-track {
  background-color: #f1f5f9;
}

/* Row Hover & Selection */
.el-table__body tr:hover > td.el-table__cell {
  background-color: #f8fafc !important;
}

.el-table__body tr.selected-row td.el-table__cell {
  background-color: #eff6ff !important; /* Light blue selection */
}

/* 操作列按钮间距 */
.el-table .el-button + .el-button {
  margin-left: 8px;
}

/* 控制输入框的调整大小 */
.input-field {
  resize: none;
  width: 100%;
}

.input-field[type="textarea"] {
  resize: both;
  min-height: 100px;
}

/* 数字输入框样式 */
.el-input-number.input-field {
  width: 100%;
}

/* 移除数字输入框的上下箭头 */
.el-input-number.input-field .el-input-number__decrease,
.el-input-number.input-field .el-input-number__increase {
  display: none;
}

/* 调整数字输入框的内边距和对齐方式 */
.el-input-number.input-field .el-input__wrapper {
  padding-left: 11px;
  padding-right: 11px;
}

/* 数字输入框右对齐 */
.el-input-number.input-field .el-input__inner {
  text-align: right !important;
}

/* 移除浏览器默认的数字输入框箭头 */
.el-input-number.input-field input[type="number"]::-webkit-outer-spin-button,
.el-input-number.input-field input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
.el-input-number.input-field input[type="number"] {
  -moz-appearance: textfield;
}

/* 自动完成下拉框样式 */
.suggestion-item {
  padding: 4px 0;
}

.suggestion-address {
  font-size: 12px;
  color: #999;
}

/* 复选框组样式 */
.checkbox-group-with-labels {
  width: 100%;
}

.checkbox-group-section {
  margin-bottom: 16px;
}

.checkbox-group-label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #EBEEF5;
}

.checkbox-group-section .el-checkbox {
  margin-right: 16px;
  margin-bottom: 8px;
}

.checkbox-group-section .el-checkbox:last-child {
  margin-right: 0;
}

/* 添加到现有的 style 部分 */
.el-table .cell {
  text-align: inherit !important;
}

.el-table .is-right {
  text-align: right !important;
}

.el-table .is-left {
  text-align: left !important;
}

/* 防止表单标签折行 */
.base-data-view .el-form-item__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 导入错误对话框的主容器样式 */
body .el-message-box.import-error-dialog {
    /* 设置对话框的宽度 */
    width: 600px !important;     /* 修改为 600px */
    max-width: 95vw;            /* 防止在小屏幕上超出视窗 */
    min-width: 400px;           /* 调整最小宽度 */
    
    /* 固定定位在屏幕中央 */
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;  /* 使用 transform 实现精确居中 */
    margin: 0 !important;
    
    /* 弹窗基本设置 */
    resize: none;             /* 禁用大小调整功能 */
    display: flex;            /* 使用 flex 布局 */
    flex-direction: column;   /* 垂直方向排列子元素 */
    height: 400px;           /* 修改为 400px */
    overflow: hidden;        /* 防止内容溢出 */
}

/* 对话框标题区域样式 */
.import-error-dialog .el-message-box__header {
    flex: none;              /* 防止标题区域被压缩或拉伸 */
    height: 40px;            /* 减小标题高度 */
    padding: 10px 15px;      /* 适当减小内边距 */
    box-sizing: border-box;  /* 将padding计入总高度 */
    border-bottom: 1px solid #ebeef5;  /* 底部边框线 */
    display: flex;           /* 使用 flex 布局 */
    align-items: center;     /* 垂直居中 */
    justify-content: space-between;  /* 标题和关闭按钮两端对齐 */
}

/* 对话框内容区域容器 */
.import-error-dialog .el-message-box__content {
    flex: 1;                /* 占据剩余所有空间 */
    min-height: 0;          /* 允许内容区域在flex布局中收缩 */
    position: relative;     /* 为绝对定位的子元素提供参考 */
    padding: 0;
    margin: 0;
}

/* 消息内容区域 */
.import-error-dialog .el-message-box__message {
    /* 绝对定位，填充整个内容区域 */
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;       /* 只在垂直方向显示滚动条 */
    margin: 0;
    padding: 0;
}

/* 具体的内容样式 */
.import-error-content {
    padding: 15px 20px;     /* 略微减小内边距 */
    background-color: #fff;
    color: #303133;
    /* 设置字体 */
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 14px;
    line-height: 1.8;
    width: 100%;
    box-sizing: border-box;
    white-space: pre-wrap;  /* 保留换行和空格 */
    word-break: break-word; /* 允许在单词内换行 */
}

/* 按钮区域样式 */
.import-error-dialog .el-message-box__btns {
    flex: none;             /* 防止按钮区域被压缩或拉伸 */
    height: 50px;           /* 减小按钮区域高度 */
    padding: 10px 15px;     /* 适当减小内边距 */
    border-top: 1px solid #ebeef5;  /* 顶部边框线 */
    margin: 0;
    display: flex;          /* 使用 flex 布局 */
    align-items: center;    /* 垂直居中 */
    justify-content: flex-end;  /* 按钮靠右对齐 */
    background-color: #fff;
}

/* 自定义滚动条样式 */
.import-error-dialog .el-message-box__message::-webkit-scrollbar {
    width: 6px;             /* 滚动条宽度 */
}

/* 滚动条滑块样式 */
.import-error-dialog .el-message-box__message::-webkit-scrollbar-thumb {
    background: #909399;     /* 滚动条颜色 */
    border-radius: 3px;      /* 圆角 */
}

/* 滚动条轨道样式 */
.import-error-dialog .el-message-box__message::-webkit-scrollbar-track {
    background: #f5f7fa;     /* 轨道背景色 */
}
/* 自定义半透明 Tooltip 样式 */
/* 自定义半透明 Tooltip 样式 - Light Glassmorphism */
.el-popper.glass-tooltip-popper {
  background: rgba(255, 255, 255, 0.95) !important; /* 高亮半透明白 */
  backdrop-filter: blur(8px); /* 更强的模糊 */
  border: 1px solid rgba(255, 255, 255, 0.5); /* 柔和边框 */
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12); /* 柔和且深邃的阴影 */
  color: #334155 !important; /* 深灰色文字 */
  max-width: 400px;
  line-height: 1.6;
  padding: 12px 16px; /* 增加内边距 */
  border-radius: 8px; /* 圆角 */
}

/* 箭头颜色适配白色背景 */
.el-popper.glass-tooltip-popper .el-popper__arrow::before {
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: none; /* 移除箭头阴影避免穿帮 */
}

.rich-tooltip-content {
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 14px; /* 稍微加大文字 */
  color: #334155;
}

.truncate-cell-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  cursor: pointer;
}

/* ------------------- Dialog Modernization ------------------- */
.el-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.el-dialog__header {
  margin: 0;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  display: flex;
  align-items: center;
}

.el-dialog__title {
  font-weight: 600;
  font-size: 18px;
  color: #1f2937;
  line-height: 1.5;
}

.el-dialog__body {
  padding: 24px;
}

.el-dialog__footer {
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #f0f0f0;
}

/* Form Item Styling inside Dialog */
.el-form-item {
  margin-bottom: 22px;
}

.el-form-item__label {
  font-weight: 500;
  color: #374151;
}

.el-input__wrapper, .el-select__wrapper, .el-textarea__inner {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  border-radius: 6px;
}

/* Hover/Focus states for inputs */
.el-input__wrapper:hover, .el-select__wrapper:hover {
  box-shadow: 0 0 0 1px #d1d5db inset;
}

.el-input__wrapper.is-focus, .el-select__wrapper.is-focus {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2) !important;
  border-color: #3b82f6;
}

/* ------------------- Enhanced Select Group Styles ------------------- */
.el-select-group__title {
  font-size: 13px !important;
  color: #111827 !important; /* Darker color for visibility */
  font-weight: 700 !important;
  padding-left: 12px !important;
  line-height: 32px !important;
  background-color: #f3f4f6; /* Subtle background for the header */
  border-bottom: 1px solid #e5e7eb;
  margin-top: 4px;
}

.el-select-group__wrap:not(:last-of-type) {
  padding-bottom: 4px; /* Space between groups */
}

/* Indent options within group slightly */
.el-select-group .el-select-dropdown__item {
  padding-left: 24px !important; 
}
</style>
